import usePosOrderMenuValues from "@/shared/atoms/usePosOrderMenuValuesAtom";
import { useSearchText } from "@/shared/atoms/usePosOrderSearchAtom";
import useCategoryQueryHook from "@/shared/hooks/useCategoryQueryHook";
import {
  filterMenuItemByName,
  isFavoriteCategory,
  sortFavoriteCategoriesFirst,
} from "@/shared/utils/helpers";
import { Fragment, useMemo } from "react";
import PosMenuItem from "./PosMenuItem";
import { PosSettings } from "@/shared/utils/interfaces";
import { cn } from "@/shared/utils/cn";

const CategoryCardLeft = ({ settings }: { settings: PosSettings }) => {
  const { categories, products } = useCategoryQueryHook();
  const [{ selectedCat }] = usePosOrderMenuValues();
  const text = useSearchText();

  const {
    color_palette,
    column_count_per_category,
    product_column_count,
    is_cart_on_right,
  } = settings;

  const filteredCategories = useMemo(
    () =>
      sortFavoriteCategoriesFirst(
        (categories ?? []).filter((cat) => filterMenuItemByName(cat.name, text))
      ),
    [categories, text]
  );

  const filteredSelectedCatProducts = useMemo(
    () =>
      selectedCat?.sub_content?.filter((product) =>
        filterMenuItemByName(product.name, text)
      ) || [],
    [selectedCat?.sub_content, text]
  );

  const filteredProducts = useMemo(
    () =>
      products?.filter((product) => filterMenuItemByName(product.name, text)) ||
      [],
    [products, text]
  );

  return (
    <div
      className={cn(
        "mt-4 flex h-full gap-4",
        is_cart_on_right ? "flex-row" : "flex-row-reverse"
      )}
    >
      <div className="w-[25%] overflow-y-auto">
        <div className="grid grid-cols-1 justify-start gap-2">
          {filteredCategories.map((cat, index) => (
            <PosMenuItem
              key={cat.item_id}
              item={cat}
              isFavorite={isFavoriteCategory(cat.virtual_item_type)}
              bgColor={color_palette[index % 8]}
              settings={settings}
            />
          ))}
        </div>
      </div>

      <div className="w-[75%] overflow-y-auto">
        <div
          className={cn(
            "grid gap-2",
            `md:grid-cols-${column_count_per_category?.[selectedCat?.item_id] || product_column_count}`
          )}
        >
          {selectedCat
            ? filteredSelectedCatProducts.map((product, index) => (
                <Fragment key={product.item_id}>
                  <PosMenuItem
                    item={product}
                    settings={settings}
                    bgColor={color_palette[index % 8]}
                  />
                  {filteredSelectedCatProducts.length === index + 1 && (
                    <PosMenuItem
                      isLast
                      item={{ ...product, parent_id: selectedCat.item_id }}
                      settings={settings}
                      bgColor={color_palette[index % 8]}
                    />
                  )}
                </Fragment>
              ))
            : filteredProducts.map((product, index) => (
                <PosMenuItem
                  key={product.item_id}
                  item={product}
                  visible={text?.length > 0}
                  settings={settings}
                  bgColor={color_palette[index % 8]}
                />
              ))}
        </div>
      </div>
    </div>
  );
};

export default CategoryCardLeft;
